import { roundPriceCents } from '@bondsports/general';
import { CartDiscountDto, CartDto, DiscountOnEnum } from '@bondsports/types';
import { Discount, Percent, Text } from '@components/atoms';
import { Price } from '@components/molecules';
import * as React from 'react';
import type { ICartSummaryFee } from '../types/interface';
import { ExtendedSummary } from './components/extended-summary';
import { defaultLabels, type SummaryLabels } from './labels/labels';
import { getDiscountLabel, getTaxLabel } from './utils/utils';

interface CartSummaryProps {
	cart: CartDto;
	onRemoveDiscount?: (discount: CartDiscountDto) => void;
	labels?: SummaryLabels;
	fee?: ICartSummaryFee;
	withoutTrailingZeros?: boolean;
}

const CartSummary = ({ cart, onRemoveDiscount, labels, fee, withoutTrailingZeros = false }: CartSummaryProps) => {
	const summaryLabels = { ...defaultLabels, ...labels };
	const discountsList = React.useMemo(() => {
		return cart.discounts?.map((discount, index) => ({
			id: index,
			label: getDiscountLabel(discount, cart.currency),
			amount: discount.discountAmount,
			onRemove: discount.discountOn === DiscountOnEnum.ALL ? () => onRemoveDiscount?.(discount) : undefined,
		}));
	}, [cart]);

	const totalCartPrice = React.useMemo(() => {
		if (!fee?.amount) {
			return cart.price ?? 0;
		}

		return roundPriceCents((cart.price || 0) + fee.amount);
	}, [cart.price, fee?.amount]);

	const taxesList = React.useMemo(() => {
		return cart.taxes?.map((tax, index) => ({
			id: tax.id ?? index,
			label: getTaxLabel(tax),
			amount: tax.price,
		}));
	}, [cart]);

	return (
		<div className="flex flex-col gap-1.5">
			<div className="flex justify-between">
				<Text size="sm">{summaryLabels.subtotal}</Text>
				<Price
					data-testid="subtotal-cart"
					size="sm"
					currency={cart.currency}
					price={cart.subtotal}
					withoutTrailingZeros={withoutTrailingZeros}
				/>
			</div>
			{cart?.discounts?.length ? (
				<ExtendedSummary
					amount={cart.discountSubtotal}
					currency={cart.currency}
					label={summaryLabels.discounts}
					icon={<Discount />}
					summaryList={discountsList}
					withoutTrailingZeros={withoutTrailingZeros}
				/>
			) : null}
			{cart.taxAmount && cart.taxAmount > 0 ? (
				<ExtendedSummary
					amount={cart.taxAmount}
					currency={cart.currency}
					label={summaryLabels.tax}
					icon={<Percent />}
					summaryList={taxesList}
					withoutTrailingZeros={withoutTrailingZeros}
				/>
			) : null}
			{fee && fee.amount > 0 ? (
				<ExtendedSummary
					amount={fee.amount}
					currency={cart.currency}
					label={summaryLabels.fees(fee.percentage)}
					icon={fee.icon}
					withoutTrailingZeros={withoutTrailingZeros}
				/>
			) : null}
			<div className="flex justify-between">
				<Text weight="medium">{summaryLabels.total}</Text>
				<Price
					data-testid="total-cart-price"
					weight="medium"
					currency={cart.currency}
					price={totalCartPrice}
					withoutTrailingZeros={withoutTrailingZeros}
				/>
			</div>
		</div>
	);
};

export { CartSummary };
